import { flow, getParent, type Instance, types } from 'mobx-state-tree';

import { fetchApplicationAttachmentsV2 } from 'src/graphql/fetchApplicationAttachmentsV2.hirer.graphql';
import { deleteApplicationAttachment } from 'src/graphql/hirer/deleteApplicationAttachment';
import type { PageConfig } from 'src/libs/PageConfig';
import { mapGraphAttachmentTypeToStore } from 'src/stores/JobApplicationStore/utils/mapGraphAttachmentTypeToStore';

import {
  HIRER_UPLOADED,
  isApplicationDocuments,
} from '../../constants/attachmentTypes';
import { addAttachmentService } from '../../services/attachmentService';
import {
  trackAddedAttachment,
  trackDeleteAttachment,
} from '../../utils/events';
import {
  ACTION_DONE,
  ACTION_ERROR,
  ACTION_VALIDATION_ERROR,
  STATE_ERROR,
  STATE_LOADED,
  stateType,
} from '../state';

import Attachment, { type IAttachment } from './Attachment';
import type { IJobApplication } from './JobApplication';

const mapToAttachment = (
  attachment: Awaited<ReturnType<typeof fetchApplicationAttachmentsV2>>[0],
  isNew?: boolean,
) =>
  Attachment.create({
    id: attachment.blobId,
    type: mapGraphAttachmentTypeToStore(attachment.attachmentType),
    isPdf: attachment.hasConvertedPdf,
    filename: attachment.fileName,
    legacyId: attachment.legacyAttachmentId,
    uploadedDate: attachment.uploadedDate,
    uploadedUser: attachment.uploadedUser,
    isNew,
  });

const AttachmentsStore = types
  .model('Attachments', {
    attachmentList: types.array(Attachment),
    state: stateType,
  })
  .views((self) => ({
    get jobApplication(): IJobApplication {
      return getParent(self) as IJobApplication;
    },
  }))
  .views((self) => ({
    get hirerUploadedDocuments() {
      return self.attachmentList
        .filter(
          (attachment: IAttachment) => attachment.type === HIRER_UPLOADED.type,
        )
        .sort((attachment1: IAttachment, attachment2: IAttachment) =>
          (attachment2.uploadedDate || '').localeCompare(
            attachment1.uploadedDate || '',
          ),
        );
    },
    get applicationDocuments() {
      return self.attachmentList.filter((attachment: IAttachment) =>
        isApplicationDocuments(attachment.type),
      );
    },
  }))
  .actions((self) => {
    const loadAttachments: ({
      jobId,
      applicationCorrelationId,
      addCustomError,
    }: {
      jobId: string;
      applicationCorrelationId: string;
      addCustomError: PageConfig['dataDogRum']['addCustomError'];
    }) => Promise<void> = flow(function* ({
      jobId,
      applicationCorrelationId,
      addCustomError,
    }: {
      jobId: string;
      applicationCorrelationId: string;
      addCustomError: PageConfig['dataDogRum']['addCustomError'];
    }) {
      try {
        const attachments: Awaited<
          ReturnType<typeof fetchApplicationAttachmentsV2>
        > = yield fetchApplicationAttachmentsV2({
          input: {
            applicationCorrelationId,
            jobId,
          },
        });

        self.attachmentList.replace(attachments.map((a) => mapToAttachment(a)));
        self.state = STATE_LOADED;
      } catch (error: any) {
        self.state = STATE_ERROR;
        addCustomError({
          error,
          context: {
            activity: 'LOAD_ATTACHMENTS',
            description: 'Error when attempting: fetchApplicationAttachmentsV2',
          },
        });
      }
    });

    const addAttachment: ({
      jobId,
      applicationCorrelationId,
      file,
      addCustomError,
    }: {
      jobId: string;
      applicationCorrelationId: string;
      file: File;
      addCustomError: PageConfig['dataDogRum']['addCustomError'];
    }) => Promise<'done' | 'validation_error' | 'error'> = flow(function* ({
      jobId,
      applicationCorrelationId,
      file,
      addCustomError,
    }: {
      jobId: string;
      applicationCorrelationId: string;
      file: File;
      addCustomError: PageConfig['dataDogRum']['addCustomError'];
    }) {
      try {
        const attachments: Awaited<ReturnType<typeof addAttachmentService>> =
          yield addAttachmentService({
            jobId,
            applicationCorrelationId,
            prospectId: self.jobApplication.id,
            file,
          });

        self.attachmentList.replace(
          attachments.map((a) => mapToAttachment(a, a.isNew)),
        );

        trackAddedAttachment(self.jobApplication.baseTrackingAttributes);
        return ACTION_DONE;
      } catch (error: any) {
        addCustomError({
          error,
          context: {
            activity: 'ADD_ATTACHMENT',
            description: 'Error when attempting: addAttachmentService',
          },
        });
        const limitErrorMessage = 'Prospect attachment limit';
        if (
          error.message.length === 0 ||
          error.message.indexOf(limitErrorMessage) > -1
        ) {
          return ACTION_VALIDATION_ERROR;
        }
        return ACTION_ERROR;
      }
    });

    const deleteAttachment: ({
      legacyAttachmentId,
      blobId,
      addCustomError,
    }: {
      legacyAttachmentId: string | null;
      blobId: string;
      addCustomError: PageConfig['dataDogRum']['addCustomError'];
    }) => Promise<'done' | 'error'> = flow(function* ({
      legacyAttachmentId,
      blobId,
      addCustomError,
    }: {
      legacyAttachmentId: string | null;
      blobId: string;
      addCustomError: PageConfig['dataDogRum']['addCustomError'];
    }) {
      try {
        if (legacyAttachmentId === null) {
          throw new Error('legacyAttachmentId is null');
        }

        const result = yield deleteApplicationAttachment(
          self.jobApplication.id,
          legacyAttachmentId,
        );
        if (!result) {
          throw new Error('attachment deletion api return false');
        }
        self.attachmentList.forEach((attachment: IAttachment) => {
          if (attachment.legacyId === legacyAttachmentId) {
            self.attachmentList.remove(attachment);
          }
        });

        trackDeleteAttachment({
          applicationCorrelationId:
            self.jobApplication.applicationCorrelationId,
          applicationId: self.jobApplication.jobApplicationId,
          blobId,
          seekCandidateId: self.jobApplication.seekCandidateId,
          adcentreCandidateId: self.jobApplication.candidateId,
        });
        return ACTION_DONE;
      } catch (error: any) {
        addCustomError({
          error,
          context: {
            activity: 'DELETE_ATTACHMENT',
            description: 'Error when attempting: deleteApplicationAttachment',
          },
        });
        return ACTION_ERROR;
      }
    });
    return {
      loadAttachments,
      addAttachment,
      deleteAttachment,
    };
  });

export interface IAttachmentsStore extends Instance<typeof AttachmentsStore> {}

export default AttachmentsStore;
